// What the page notices quietly while a learner works on a question: how long
// it was on screen (paused while the tab is hidden), how often and how long
// they left the tab, and whether they pasted into the answer box. Sent with
// each attempt; the server clamps every number and CMS turns them into review
// flags for admins. Never shown to the learner as a countdown, and never used
// to change marks (design: 30-projects/practice-sessions-design.md).
//
// Pure and clock-injected so it can be tested without a browser.

export interface AttemptSignalSnapshot {
  timeOnQuestionMs: number
  pasted: boolean
  tabAwayCount: number
  tabAwayMs: number
  activeMsDelta: number
}

interface QuestionStats {
  visibleMs: number
  tabAwayCount: number
  tabAwayMs: number
  pasted: boolean
}

export class AttemptSignals {

  private readonly stats = new Map<string, QuestionStats>();

  private current: string | null = null;

  private visible: boolean;

  /** Start of the running on-screen stretch for the current question. */
  private visibleSince: number | null = null;

  private hiddenSince: number | null = null;

  /** On-screen time not yet reported to the server. */
  private unsentActiveMs = 0;

  constructor(private readonly now: () => number = () => Date.now(), visible = true) {

    this.visible = visible;
  }

  private statsFor(questionId: string): QuestionStats {

    let entry = this.stats.get(questionId);

    if (!entry) {

      entry = {
        visibleMs: 0,
        tabAwayCount: 0,
        tabAwayMs: 0,
        pasted: false
      };
      this.stats.set(questionId, entry);
    }

    return entry;
  }

  /** Bank the running stretch and, if still on screen, start a new one. */
  private flush(): void {

    const at = this.now();

    if (this.current !== null && this.visibleSince !== null) {

      const elapsed = Math.max(0, at - this.visibleSince);
      this.statsFor(this.current).visibleMs += elapsed;
      this.unsentActiveMs += elapsed;
    }

    this.visibleSince = this.visible && this.current !== null ? at : null;
  }

  /** The learner is now looking at this question. */
  focus(questionId: string): void {

    this.flush();
    this.current = questionId;
    this.visibleSince = this.visible ? this.now() : null;
  }

  /** The tab was hidden (another tab, another app, the phone locked). */
  hide(): void {

    if (!this.visible) {

      return;
    }

    this.flush();
    this.visible = false;
    this.visibleSince = null;
    this.hiddenSince = this.now();

    if (this.current !== null) {

      this.statsFor(this.current).tabAwayCount += 1;
    }
  }

  /** The tab is visible again. */
  show(): void {

    if (this.visible) {

      return;
    }

    const at = this.now();

    if (this.current !== null && this.hiddenSince !== null) {

      this.statsFor(this.current).tabAwayMs += Math.max(0, at - this.hiddenSince);
    }

    this.visible = true;
    this.hiddenSince = null;
    this.visibleSince = this.current !== null ? at : null;
  }

  /** Something was pasted into this question's answer box. */
  paste(questionId: string): void {

    this.statsFor(questionId).pasted = true;
  }

  /**
   * The signals to send with an attempt at this question. Time and tab-away
   * figures are totals for the question so far; `pasted` covers the paste
   * since the last attempt; `activeMsDelta` is on-screen time since the last
   * report, across questions, so the session total adds up once.
   */
  snapshot(questionId: string): AttemptSignalSnapshot {

    this.flush();

    const stats = this.statsFor(questionId);
    const out: AttemptSignalSnapshot = {
      timeOnQuestionMs: Math.round(stats.visibleMs),
      pasted: stats.pasted,
      tabAwayCount: stats.tabAwayCount,
      tabAwayMs: Math.round(stats.tabAwayMs),
      activeMsDelta: Math.round(this.unsentActiveMs)
    };

    stats.pasted = false;
    this.unsentActiveMs = 0;

    return out;
  }
}
